import { formatDate, type RecordStamps } from "@nexseed/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { Ban, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/app/dialogs";
import { EmptyState } from "@/components/app/empty-state";
import { FormStamps, StampLines } from "@/components/app/record-stamps";
import { PageHeader } from "@/components/app/page-header";
import { SearchField } from "@/components/app/search-field";
import { Button } from "@/components/ui/button";
import { Callout } from "@/components/ui/callout";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card";
import { Field } from "@/components/ui/field";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";
import { Tooltip } from "@/components/ui/tooltip";
import { useAuth } from "@/features/auth/store";
import { useReferenceNames } from "@/features/masters/api";
import { ReferenceCombobox } from "@/components/app/reference-combobox";
import { MASTERS } from "@/features/masters/registry";
import { api, ApiError } from "@/lib/api-client";

export const Route = createFileRoute("/_app/masters/storage-restrictions")({
  // Both special screens carry their own codes now, so the URL is guarded too — the side rail
  // hiding an entry has never stopped anyone typing the path.
  beforeLoad: () => {
    if (!useAuth.getState().can("storage_restrictions.view"))
      throw redirect({ to: "/" });
  },
  component: Page,
});

interface Pair {
  stamps?: RecordStamps;
  varietyAId: number;
  varietyBId: number;
  createdAt: string;
}

/** Variety pairs that must never share a bin (checked at put-away, B22). */
function Page() {
  const qc = useQueryClient();
  const manage = useAuth((s) => s.can("storage_restrictions.manage"));
  const key = ["masters", "storage-restrictions"] as const;
  const pairs = useQuery({
    queryKey: key,
    queryFn: () => api<Pair[]>("/masters/storage-restrictions"),
  });
  const names = useReferenceNames(
    MASTERS.varieties,
    (pairs.data ?? []).flatMap((p) => [p.varietyAId, p.varietyBId])
  );
  const [a, setA] = useState<number | null>(null);
  const [b, setB] = useState<number | null>(null);
  const [q, setQ] = useState("");
  const [confirm, setConfirm] = useState<Pair | null>(null);
  const add = useMutation({
    mutationFn: () =>
      api<Pair>("/masters/storage-restrictions", {
        method: "POST",
        json: { varietyAId: a, varietyBId: b },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key });
      toast.success("Restriction added");
      setA(null);
      setB(null);
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "Could not add"),
  });
  const remove = useMutation({
    mutationFn: (p: Pair) =>
      api<void>("/masters/storage-restrictions", {
        method: "DELETE",
        json: { varietyAId: p.varietyAId, varietyBId: p.varietyBId },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key });
      toast.success("Restriction removed");
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "Could not remove"),
  });
  const label = (id: number) => names.data?.get(id) ?? `#${id}`;
  const rows = (pairs.data ?? []).filter(
    (p) =>
      !q ||
      `${label(p.varietyAId)} ${label(p.varietyBId)}`
        .toLowerCase()
        .includes(q.toLowerCase())
  );
  const selfPair = a !== null && a === b;
  return (
    <>
      <PageHeader
        title="Storage restrictions"
        description="Pairs of varieties that must never share a bin. Put-away refuses a bin that already holds the other variety. (Variety Storage Restrictions in vnr-prod.)"
      />
      <div className="grid gap-4 lg:grid-cols-[22rem_1fr]">
        {manage && (
          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle>Add a pair</CardTitle>
            </CardHeader>
            <CardBody className="space-y-3">
              <FormStamps />
              <Field label="Variety A" htmlFor="sr-a" required>
                <ReferenceCombobox
                  id="sr-a"
                  master="varieties"
                  value={a}
                  onChange={setA}
                  ariaLabel="Variety A"
                />
              </Field>
              <Field
                label="Variety B"
                htmlFor="sr-b"
                required
                error={
                  selfPair
                    ? "A variety cannot be restricted against itself"
                    : undefined
                }
              >
                <ReferenceCombobox
                  id="sr-b"
                  master="varieties"
                  value={b}
                  onChange={setB}
                  ariaLabel="Variety B"
                  invalid={selfPair}
                />
              </Field>
              <Button
                className="w-full"
                disabled={a === null || b === null || selfPair}
                loading={add.isPending}
                onClick={() => add.mutate()}
              >
                <Plus className="h-4 w-4" /> Add restriction
              </Button>
              <Callout tone="info">
                The pair is symmetric and stored once (A &lt; B).
              </Callout>
            </CardBody>
          </Card>
        )}
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle>Restricted pairs</CardTitle>
            <SearchField
              value={q}
              onChange={setQ}
              placeholder="Filter by variety"
              hotkey={null}
              className="sm:w-60"
            />
          </CardHeader>
          <CardBody className="p-0">
            {rows.length === 0 ? (
              <EmptyState
                icon={Ban}
                title={q ? "No pairs match" : "No restrictions yet"}
                description="An empty list means any two varieties may share a bin."
                compact
              />
            ) : (
              <Table>
                <THead className="bg-surface-header text-xs font-bold tracking-normal text-ink-muted normal-case [&_th]:h-9 [&_th]:border-b [&_th]:border-border">
                  <tr>
                    <TH>Variety A</TH>
                    <TH>Variety B</TH>
                    <TH>Since</TH>
                    {manage && <TH>Action</TH>}
                  </tr>
                </THead>
                <TBody>
                  {rows.map((p) => (
                    <TR key={`${p.varietyAId}-${p.varietyBId}`}>
                      <TD>
                        <div className="flex flex-wrap items-center gap-1">
                          <span>{label(p.varietyAId)}</span>
                          <StampLines stamps={p.stamps} />
                        </div>
                      </TD>
                      <TD>{label(p.varietyBId)}</TD>
                      <TD className="text-ink-muted">
                        {formatDate(p.createdAt)}
                      </TD>
                      {manage && (
                        <TD align="right">
                          <Tooltip content="Remove">
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              aria-label={`Remove ${label(p.varietyAId)} / ${label(p.varietyBId)}`}
                              onClick={() => setConfirm(p)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </Tooltip>
                        </TD>
                      )}
                    </TR>
                  ))}
                </TBody>
              </Table>
            )}
          </CardBody>
        </Card>
      </div>
      <ConfirmDialog
        open={confirm !== null}
        title="Remove this restriction?"
        confirmLabel="Remove"
        danger
        onClose={() => setConfirm(null)}
        onConfirm={() => confirm && remove.mutate(confirm)}
      />
    </>
  );
}
